import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShieldCheck, ShieldX, FileText, Loader2, Phone, Mail, MapPin, Car } from 'lucide-react';
import api from '../api';

const condLabel = (c) => c === 'used_oem' ? 'Used OEM' : c === 'refurbished' ? 'Refurbished' : 'New';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function VerifyInvoicePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        const { data } = await api.get(`/verify/${token}`);
        if (data.verified) {
          setInvoice(data.invoice);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center" data-testid="verify-loading">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-[#0F766E] mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Verifying invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="verify-error">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldX size={48} className="text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Verification Failed</h1>
          <p className="text-gray-500 mb-6">This invoice could not be verified. The verification code may be invalid or the invoice does not exist in our system.</p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700 font-medium">This document is NOT verified by Martinez Auto Parts</p>
          </div>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0F766E] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#0D5F59] transition-colors" data-testid="verify-go-home">
            Visit Martinez Auto Parts
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="verify-success">
      {/* Verification Badge */}
      <div className="bg-gradient-to-r from-[#0F766E] to-[#0D9488] rounded-xl p-6 mb-8 text-white" data-testid="verification-badge">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center shrink-0">
            <ShieldCheck size={36} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Verified Authentic Invoice</h1>
            <p className="text-white/80 text-sm mt-1">This invoice has been verified and is certified by Martinez Auto Parts. Invoice <span className="font-mono font-bold text-white">{invoice.invoice_number}</span></p>
          </div>
        </div>
      </div>

      {/* Digital Invoice */}
      <div className="bg-white border rounded-xl shadow-lg overflow-hidden" data-testid="verified-invoice-document">
        <div className="p-8 sm:p-10">
          {/* Header */}
          <div className="flex justify-between items-start mb-9 border-b-[3px] border-[#0F766E] pb-6">
            <div>
              <img src="/logo-light.png" alt="Martinez Auto Parts" className="h-16 w-auto mb-2" />
              {invoice.company_phone && <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Phone size={10} /> {invoice.company_phone}</p>}
              {invoice.company_email && <p className="text-xs text-gray-500 flex items-center gap-1"><Mail size={10} /> {invoice.company_email}</p>}
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black text-[#0F766E] leading-none">INVOICE</h2>
              <p className="text-sm font-mono font-bold text-gray-700 mt-2">{invoice.invoice_number}</p>
              <p className="text-xs text-gray-500 mt-1">{new Date(invoice.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <span className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full ${statusColors[invoice.status] || statusColors.draft}`}>
                {invoice.status?.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Customer & Vehicle */}
          <div className="flex justify-between mb-9">
            <div className="w-1/2">
              <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Bill To</h3>
              <p className="font-bold text-gray-900 text-base">{invoice.customer_name}</p>
              {invoice.customer_phone && <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-1"><Phone size={12} className="text-gray-400" /> {invoice.customer_phone}</p>}
              {invoice.customer_email && <p className="text-sm text-gray-600 flex items-center gap-1"><Mail size={12} className="text-gray-400" /> {invoice.customer_email}</p>}
              {invoice.customer_address && <p className="text-sm text-gray-600 flex items-center gap-1"><MapPin size={12} className="text-gray-400" /> {invoice.customer_address}</p>}
            </div>
            {invoice.vehicle_info && (
              <div className="text-right">
                <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Vehicle</h3>
                <p className="font-bold text-gray-900 text-base flex items-center gap-1 justify-end"><Car size={14} className="text-[#0F766E]" /> {invoice.vehicle_info}</p>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse mb-8">
              <thead>
                <tr>
                  <th className="bg-[#0F766E] text-white px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider rounded-tl">Description</th>
                  <th className="bg-[#0F766E] text-white px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider">OEM #</th>
                  <th className="bg-[#0F766E] text-white px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider">Condition</th>
                  <th className="bg-[#0F766E] text-white px-4 py-3 text-center text-[10px] font-bold uppercase tracking-wider">Qty</th>
                  <th className="bg-[#0F766E] text-white px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider">Price</th>
                  <th className="bg-[#0F766E] text-white px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider rounded-tr">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, i) => (
                  <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.part_name}</td>
                    <td className="px-4 py-3 text-sm font-mono text-[#0F766E] font-bold">{item.oem_number}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{condLabel(item.condition)}</td>
                    <td className="px-4 py-3 text-sm text-center">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm text-right">${item.unit_price?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold">${(item.unit_price * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-semibold">${invoice.subtotal?.toFixed(2)}</span></div>
              {invoice.tax_rate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax ({invoice.tax_rate}%)</span><span>${invoice.tax_amount?.toFixed(2)}</span></div>}
              {invoice.discount > 0 && <div className="flex justify-between"><span className="text-gray-500">Discount</span><span className="text-red-600">-${invoice.discount?.toFixed(2)}</span></div>}
              <div className="flex justify-between border-t-[3px] border-[#0F766E] pt-3 mt-3">
                <span className="font-bold text-gray-900 text-lg">Total</span>
                <span className="font-black text-2xl text-[#0F766E]">${invoice.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-10 p-5 bg-gray-50 rounded-lg">
              <h3 className="text-[10px] font-bold uppercase tracking-[2px] text-gray-400 mb-2">Notes</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 text-center text-gray-400 text-xs border-t pt-5">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ShieldCheck size={14} className="text-[#0F766E]" />
              <span className="text-[#0F766E] font-bold">Digitally Verified Invoice</span>
            </div>
            <p className="font-medium">{invoice.company_name || 'Martinez Auto Parts LLC'}</p>
            <p className="mt-1">4720 N High St, Columbus, OH 43214</p>
            <p className="mt-1">{invoice.company_phone} | {invoice.company_email}</p>
            <p className="mt-2 text-gray-300">Thank you for your business!</p>
          </div>
        </div>
      </div>
    </div>
  );
}
